import { fDateLongNullable } from '@faks/shared';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, Button, Card, Stack, Typography, useTheme } from '@mui/material';
import { Banner } from 'components/banner/banner';
import { InfoIconMUI2 } from 'components/icons/InfoIconMUI2';
import { StyledLink } from 'components/link/styles';
import { SimpleDialogBox } from 'components/SimpleDialogBox';
import { startCase } from 'lodash';
import { useState } from 'react';
import type { GetPanelistConnectionStatusResponse } from 'types/api/pharmacies/panelist_connection';
import { LGO_LINKS } from 'utils/linkUtils';
import { PanelistConnectionStatusBanner } from './PanelistStatusComponents';
import { BlackTypo } from './styles';

type Props = {
  panelistConnection: GetPanelistConnectionStatusResponse;
  handleOnReset: () => void;
  disableDisconnectButton: boolean;
  isLoading: boolean;
};

export const PanelistConnectedCard = ({
  panelistConnection,
  handleOnReset,
  disableDisconnectButton,
  isLoading,
}: Props) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Card sx={{ p: '25px' }}>
      <Typography fontWeight="bold" color={theme.palette.grey[500]}>
        {t`CONNEXION EXTRACTEUR`}
      </Typography>

      <PanelistConnectionStatusBanner panelistConnection={panelistConnection} />

      <Banner
        title={
          <Stack gap={1}>
            <BlackTypo variant="subtitle1">
              {startCase(panelistConnection.pharmacy_panelist?.panelist || '')}
            </BlackTypo>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Stack gap={1}>
                <Box display="flex">
                  <Trans>
                    <Typography>Date d’acceptation : </Typography>
                    <BlackTypo sx={{ ml: 0.5 }}>
                      {fDateLongNullable(panelistConnection.pharmacy_panelist?.accepted_date)}
                    </BlackTypo>
                  </Trans>
                </Box>
                <Box display="flex">
                  <Trans>
                    <Typography>Type : </Typography>
                    <BlackTypo sx={{ ml: 0.5 }}>Données de vente</BlackTypo>
                  </Trans>
                </Box>
              </Stack>

              <Button
                color="error"
                onClick={() => setIsModalOpen(true)}
                disabled={disableDisconnectButton || isLoading}
              >
                {t`Déconnecter l’extracteur`}
              </Button>
            </Box>
          </Stack>
        }
        color="grey"
        sx={{ p: '25px', mt: 2 }}
      />
      <Banner
        icon={<InfoIconMUI2 fontSize="medium" sx={{ marginRight: 1 }} />}
        title={
          <>
            <Box>{t`Faks ne commercialise pas vos données de vente.`}</Box>
            <StyledLink href={LGO_LINKS.INTERCOM} target="_blank">
              {t`En savoir plus sur les engagements de Faks.`}
            </StyledLink>
          </>
        }
        color="info"
        sx={{ maxWidth: { lg: '40%' }, mt: 2 }}
      />

      {isModalOpen && (
        <SimpleDialogBox
          handleClose={() => setIsModalOpen(false)}
          title={t`Déconnecter l’extracteur`}
          description={
            <Box marginBottom={-3}>
              <Typography>{t`Êtes-vous sûr de vouloir déconnecter votre extracteur ?`}</Typography>
              <Typography>{t`Vos données de vente ne seront plus remontées automatiquement.`}</Typography>
            </Box>
          }
          cancelButton={{
            label: t`Annuler`,
            action: () => setIsModalOpen(false),
          }}
          actionButton={{
            label: t`Confirmer`,
            action: handleOnReset,
            color: 'error',
            disabled: isLoading,
            isLoading,
          }}
        />
      )}
    </Card>
  );
};
